import '../../inputs/buttons/as-icon-button.ts';
import { internalData } from '../../../store/store.ts';
import styleText from './as-session-set.css?raw';
import { asApp } from '../../../util/singletons.ts';
import { sendEv } from '../../../data/events.ts';

/** Server-side event name logged for each session button press. */
const SESSION_LOG_EVENTS: Record<string, string> = {
  'new': 'new_session',
  'restore': 'restore_session',
  'save': 'save_session',
};

export class TrSessionSet extends HTMLElement {
  static readonly tag = 'as-session-set' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private _nameDisplay: HTMLElement | null = null;
  private _nameInput: HTMLInputElement | null = null;
  private _fileInput: HTMLInputElement | null = null;

  private _removeProjectEffect: (() => void) | null = null;
  private _removeUnsavedEffect: (() => void) | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      TrSessionSet._sheet
    ];

    this.shadowRoot!.innerHTML = /*html*/`
      <div id="header-session-set">
        <as-icon-button
          file-name="header/file-new.png" black
          tooltip-dict-key="tooltip.session.new"
          tooltip-default-text="New session"
          name="new"
        ></as-icon-button>
        <as-icon-button
          file-name="header/restore.png" black
          tooltip-dict-key="tooltip.session.restore"
          tooltip-default-text="Restore session"
          name="restore"
        ></as-icon-button>
        <as-icon-button
          file-name="header/save.png" black
          tooltip-dict-key="tooltip.session.save"
          tooltip-default-text="Save session"
          name="save"
        ></as-icon-button>
        <div class="project-name">
          <span class="name-display"><as-text dict-key="session.untitled">Untitled</as-text></span>
          <input class="name-input" type="text" value="Untitled" hidden>
        </div>
      </div>
      <input type="file" accept=".asproject,.zip" id="backup-restore">
    `;

    this._nameDisplay = this.shadowRoot!.querySelector('.name-display');
    this._nameInput = this.shadowRoot!.querySelector('.name-input');
    this._fileInput = this.shadowRoot!.querySelector('#backup-restore');

    this._setupListeners();
  }

  get projectTitle(): string {
    return internalData.currentProject?.metadata.name ?? 'Untitled';
  }

  /** The project-name text input. Apps that want to react to renames should
   *  listen for `as-project-title-change` instead of touching this. */
  get projectTitleInput(): HTMLInputElement | null {
    return this._nameInput;
  }

  set projectTitle(value: string) {
    if (internalData.currentProject) {
      internalData.currentProject.metadata.name = value;
      // Nested mutation bypasses the proxy set trap; fan it out so subscribers
      // like <as-export-group> re-sync their default file name.
      internalData.notify('currentProject');
      this._refreshDisplay();
    }
  }

  private _setupListeners(): void {
    const sr = this.shadowRoot!;

    sr.addEventListener('as-button-click', (e: Event) => {
      const buttonName = (e as CustomEvent).detail?.buttonName;
      const logName = SESSION_LOG_EVENTS[buttonName];
      if (logName) asApp()?.sendLog(logName);
      switch (buttonName) {
        case 'new':
          sendEv(this, 'as-new-session');
          break;
        case 'restore':
          this._fileInput?.click();
          break;
        case 'save':
          sendEv(this, 'as-save-session');
          break;
      }
    });

    this._fileInput?.addEventListener('change', (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      (e.target as HTMLInputElement).value = '';
      if (!file) return;
      sendEv(this, 'as-restore-session-file', { detail: { blob: file } });
    });

    this._nameDisplay?.addEventListener('click', () => this._startEdit());
    this._nameInput?.addEventListener('blur', () => this._commitEdit());
    this._nameInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this._commitEdit(); }
      if (e.key === 'Escape') this._cancelEdit();
    });
  }

  private _startEdit(): void {
    const current = internalData.currentProject?.metadata.name ?? 'Untitled';
    this._nameInput!.value = current;
    this._nameDisplay!.hidden = true;
    this._nameInput!.hidden = false;
    this._nameInput!.focus();
    this._nameInput!.select();
  }

  private _commitEdit(): void {
    const newName = this._nameInput!.value.trim() || 'Untitled';
    if (internalData.currentProject) {
      internalData.currentProject.metadata.name = newName;
      internalData.currentProject.metadata.updatedAt = new Date();
      // Nested mutation bypasses the proxy set trap; fan it out so subscribers
      // like <as-export-group> re-sync their default file name.
      internalData.notify('currentProject');
    }
    // Bubbles and composed, so it reaches window: the supported hook for apps
    // that used to grab the input out of the shadow DOM.
    sendEv(this, 'as-project-title-change', { detail: { name: newName } });
    this._nameDisplay!.hidden = false;
    this._nameInput!.hidden = true;
    this._refreshDisplay();
  }

  private _cancelEdit(): void {
    this._nameDisplay!.hidden = false;
    this._nameInput!.hidden = true;
  }

  private _refreshDisplay(): void {
    const name = internalData.currentProject?.metadata.name || 'Untitled';
    const unsaved = internalData.unsavedChanges;
    if (this._nameDisplay) {
      this._nameDisplay.textContent = unsaved ? `${name} *` : name;
      this._nameDisplay.classList.toggle('unsaved', unsaved);
    }
  }

  connectedCallback(): void {
    this._removeProjectEffect = internalData.subscribeKey('currentProject', () => this._refreshDisplay());
    this._removeUnsavedEffect = internalData.subscribeKey('unsavedChanges', () => this._refreshDisplay());
    this._refreshDisplay();
  }

  disconnectedCallback(): void {
    this._removeProjectEffect?.();
    this._removeUnsavedEffect?.();
  }
}

customElements.define(TrSessionSet.tag, TrSessionSet);
